import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import {
  buildIntervalCasingGeometry,
  calculateBottomOfEquipmentCasingFriction
} from "../../casingFrictionCalculations";
import type { FluidPropertiesPipelineInputs } from "../types";

// Perfs -> bottom of equipment (the sensor if there is one, otherwise the
// motor). Perfs may be above or below the equipment; the casing friction
// calculation handles both.
export const calculatePerfsToBottomOfEquipment = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    hasSensor: boolean;
    useShroudCirculationPath: boolean;
    casingInteriorDiameterAtPerfsIn: number;
    casingInteriorDiameterAtBottomOfEquipmentRawIn: number;
    wellFlowingPressureAtPerfsPSIA: number;
    getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number;
    casingFrictionLossPSIA: number;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    includeCasingFrictionLoss,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    bottomOfEquipmentMDFt,
    bottomOfEquipmentTVDFt,
    sensorCrossSectionFt2,
    bottomOfTubingMDFt,
    tubingsDepthTVDFt,
    topTubingOuterDiameterInches,
    bottomTubingOuterDiameterInches,
    topOfPumpsMDFt,
    topOfPumpsTVDFt,
    boltOnDischargeOuterDiameterInches,
    bottomOfPumpsTVDFt,
    bottomOfPumpsMDFt,
    primaryPumpOuterDiameterInches,
    motorCrossSectionFt2,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt,
    shroudOuterDiameterIn
  } = inputs;
  const {
    hasSensor,
    useShroudCirculationPath,
    casingInteriorDiameterAtPerfsIn,
    casingInteriorDiameterAtBottomOfEquipmentRawIn,
    wellFlowingPressureAtPerfsPSIA,
    getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: startingCasingFrictionLossPSIA
  } = prev;
  let casingFrictionLossPSIA = startingCasingFrictionLossPSIA;
  let bottomOfEquipmentCasingFrictionPSIA = 0;

  const downholeWellPerfsTemperatureF = wellFlowingTemperatureForMD(
    basicData.topOfPerforationsMDFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );

  // Downhole fluid/gas properties at well perfs
  const fluidPropertiesAtWellPerfsContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtPerfsPSIA,
    downholeWellPerfsTemperatureF,
    casingInteriorDiameterAtPerfsIn,
    desiredFlowRates,
    undefined,
    undefined,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi
  );

  // This will find casing friction for if perfs are above or below bottom of equipment
  const bottomOfEquipmentCasingFrictionProperties =
    calculateBottomOfEquipmentCasingFriction({
      correlations,
      topOfPerforationsMDFt: basicData.topOfPerforationsMDFt,
      topOfPerforationsTVDFt: basicData.topOfPerforationsTVDFt,
      bottomOfEquipmentMDFt,
      bottomOfEquipmentTVDFt,
      entryRows,
      fluidPropertiesAtWellPerfsContext,
      surfaceTemperatureF: basicData.surfaceTemperatureF,
      bottomholeTemperatureF: basicData.bottomholeTemperatureF,
      casingGeometry: buildIntervalCasingGeometry(
        basicData.topOfPerforationsMDFt,
        bottomOfEquipmentMDFt,
        topCasingInteriorDiameterIn,
        topCasingLengthFt,
        bottomCasingInteriorDiameterIn,
        bottomCasingLengthFt,
        undefined,
        shroudOuterDiameterIn
      ),
      bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
      producingBottomholePressurePSIA:
        basicData.producingBottomholePressurePSIA,
      wellFlowingPressureAtPerfsPSIA,
      useShroudCirculationPath,
      shroudOuterDiameterIn,
      bottomOfTubingMDFt,
      bottomOfTubingTVDFt: tubingsDepthTVDFt,
      boltOnDischargeOuterDiameterInches,
      topOfPumpsMDFt,
      topOfPumpsTVDFt,
      bottomOfPumpsMDFt,
      bottomOfPumpsTVDFt,
      topTubingOuterDiameterInches,
      bottomTubingOuterDiameterInches,
      topTubingLengthFt: basicData.topTubingLengthFt,
      primaryPumpOuterDiameterInches,
      includeCasingFrictionLoss,
      getPressureAtReferenceTVDPSIA
    });

  bottomOfEquipmentCasingFrictionPSIA =
    bottomOfEquipmentCasingFrictionProperties.frictionPressurePSIA;

  // Perfs to bottom of equipment casing friction pressure ALWAYS get included
  // The include casing friction toggle only toggles sensor -> motor,
  // motor -> seals, seals -> intakes on/off
  const perfsToBottomOfEquipmentCasingFrictionPSIA =
    bottomOfEquipmentCasingFrictionPSIA;
  casingFrictionLossPSIA += perfsToBottomOfEquipmentCasingFrictionPSIA;

  // Call helper function to get pressure at tvd then subtract out casing friction
  const wellFlowingPressureAtBottomOfEquipmentPSIA =
    getPressureAtReferenceTVDPSIA(bottomOfEquipmentTVDFt) -
    perfsToBottomOfEquipmentCasingFrictionPSIA;

  const downholeBottomOfEquipmentTemperatureF = wellFlowingTemperatureForMD(
    bottomOfEquipmentMDFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );

  const fluidPropertiesAtBottomOfEquipmentContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtBottomOfEquipmentPSIA,
    downholeBottomOfEquipmentTemperatureF,
    casingInteriorDiameterAtBottomOfEquipmentRawIn,
    desiredFlowRates,
    fluidPropertiesAtWellPerfsContext.flowRatesInSitu,
    fluidPropertiesAtWellPerfsContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    hasSensor ? sensorCrossSectionFt2 : motorCrossSectionFt2,
    0,
    fluidPropertiesOverrides,
    condensateApi
  );
  return {
    downholeWellPerfsTemperatureF,
    fluidPropertiesAtWellPerfsContext,
    bottomOfEquipmentCasingFrictionPSIA,
    perfsToBottomOfEquipmentCasingFrictionPSIA,
    downholeBottomOfEquipmentTemperatureF,
    fluidPropertiesAtBottomOfEquipmentContext,
    casingFrictionLossPSIA
  };
};
